import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  createAutosaveScheduler,
  saveWithAuthRetry,
  type AutosaveStatus,
} from '@/lib/journal/journalAutosave';
import {
  clearJournalDraftLocal,
  journalDraftHasContent,
  journalDraftHasUserContent,
  journalDraftScopeKey,
  pickNewerJournalDraft,
  readJournalDraftLocal,
  writeJournalDraftLocal,
  type JournalDraftPayload,
  type JournalDraftScope,
} from '@/lib/journal/journalDraftStorage';
import { storedJournalHtml } from '@/lib/journal/journalRichText';

export type JournalWritingDraftStatus = AutosaveStatus;

async function fetchServerDraft(
  patientId: string,
  scopeKey: string,
): Promise<JournalDraftPayload | null> {
  const { data, error } = await supabase
    .from('journal_drafts')
    .select('title, content_md, content_html, updated_at')
    .eq('patient_id', patientId)
    .eq('scope_key', scopeKey)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    title: data.title ?? '',
    content_md: data.content_md ?? '',
    content_html: data.content_html ?? null,
    updated_at: data.updated_at,
  };
}

async function upsertServerDraft(input: {
  clinicId: string;
  userId: string;
  patientId: string;
  scopeKey: string;
  scope: JournalDraftScope;
  title: string;
  contentMd: string;
  contentHtml: string | null;
}): Promise<void> {
  const { error } = await supabase.from('journal_drafts').upsert(
    {
      clinic_id: input.clinicId,
      user_id: input.userId,
      patient_id: input.patientId,
      scope_key: input.scopeKey,
      appointment_id: input.scope.kind === 'appointment' ? input.scope.appointmentId : null,
      journal_entry_id:
        input.scope.kind === 'entry'
          ? input.scope.entryId
          : input.scope.kind === 'amendment-new'
            ? input.scope.entryId
            : null,
      amendment_id: input.scope.kind === 'amendment' ? input.scope.amendmentId : null,
      title: input.title,
      content_md: input.contentMd,
      content_html: storedJournalHtml(input.contentHtml),
    },
    { onConflict: 'user_id,patient_id,scope_key' },
  );
  if (error) throw error;
}

async function deleteStoredDraft(
  patientId: string,
  scopeKey: string,
  userId: string | null | undefined,
): Promise<void> {
  if (userId) {
    clearJournalDraftLocal(userId, patientId, scopeKey);
  }
  const { error } = await supabase
    .from('journal_drafts')
    .delete()
    .eq('patient_id', patientId)
    .eq('scope_key', scopeKey);
  if (error) throw error;
}

export function useJournalWritingDraft(opts: {
  clinicId: string;
  patientId: string;
  userId: string | null | undefined;
  scope: JournalDraftScope;
  title: string;
  contentMd: string;
  contentHtml: string | null;
  /** Continuously persist while the editor is open. */
  enabled: boolean;
  /** Look for an unfinished draft even before the editor is opened. */
  restoreOnMount?: boolean;
  /** Prefill title that must not count as a user edit. */
  baselineTitle?: string;
}) {
  const { clinicId, patientId, userId, scope, title, contentMd, contentHtml, enabled } = opts;
  const restoreOnMount = opts.restoreOnMount ?? enabled;
  const baselineTitle = opts.baselineTitle ?? '';
  const scopeKey = journalDraftScopeKey(scope);
  const [status, setStatus] = useState<JournalWritingDraftStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [restored, setRestored] = useState<JournalDraftPayload | null>(null);
  const restoredChecked = useRef(false);
  const hadSavedDraft = useRef(false);
  const restoreKey = `${userId ?? ''}:${patientId}:${scopeKey}`;
  const latest = useRef({
    title,
    contentMd,
    contentHtml,
    clinicId,
    patientId,
    userId,
    scope,
    scopeKey,
    baselineTitle,
  });
  latest.current = {
    title,
    contentMd,
    contentHtml,
    clinicId,
    patientId,
    userId,
    scope,
    scopeKey,
    baselineTitle,
  };
  const schedulerRef = useRef<ReturnType<typeof createAutosaveScheduler> | null>(null);

  const persistLocal = useCallback(() => {
    const current = latest.current;
    if (!current.userId) return;
    if (!journalDraftHasUserContent(current, current.baselineTitle)) {
      clearJournalDraftLocal(current.userId, current.patientId, current.scopeKey);
      return;
    }
    const payload: JournalDraftPayload = {
      title: current.title,
      content_md: current.contentMd,
      content_html: storedJournalHtml(current.contentHtml),
      updated_at: new Date().toISOString(),
    };
    writeJournalDraftLocal(current.userId, current.patientId, current.scopeKey, payload);
  }, []);

  const persistServer = useCallback(async () => {
    const current = latest.current;
    if (!current.userId) return;
    if (!journalDraftHasUserContent(current, current.baselineTitle)) {
      if (!hadSavedDraft.current) return;
      await deleteStoredDraft(current.patientId, current.scopeKey, current.userId);
      hadSavedDraft.current = false;
      return;
    }
    await saveWithAuthRetry(
      () =>
        upsertServerDraft({
          clinicId: current.clinicId,
          userId: current.userId!,
          patientId: current.patientId,
          scopeKey: current.scopeKey,
          scope: current.scope,
          title: current.title,
          contentMd: current.contentMd,
          contentHtml: current.contentHtml,
        }),
      () => supabase.auth.refreshSession(),
    );
    hadSavedDraft.current = true;
  }, []);

  useEffect(() => {
    if (!enabled || !userId) return;
    const scheduler = createAutosaveScheduler(async () => {
      const current = latest.current;
      const hasUser = journalDraftHasUserContent(current, current.baselineTitle);
      if (!hasUser && !hadSavedDraft.current) return;
      persistLocal();
      await persistServer();
    });
    schedulerRef.current = scheduler;
    const tick = window.setInterval(() => {
      setStatus(scheduler.getStatus());
      setLastSavedAt(scheduler.getLastSavedAt());
    }, 250);
    return () => {
      window.clearInterval(tick);
      void scheduler.flush();
      scheduler.dispose();
      if (schedulerRef.current === scheduler) schedulerRef.current = null;
    };
  }, [enabled, userId, persistLocal, persistServer]);

  useEffect(() => {
    if (!enabled || !userId) return;
    const hasUser = journalDraftHasUserContent({ title, content_md: contentMd }, baselineTitle);
    if (!hasUser && !hadSavedDraft.current) return;
    persistLocal();
    schedulerRef.current?.notifyChange();
  }, [baselineTitle, enabled, userId, title, contentMd, contentHtml, persistLocal]);

  useEffect(() => {
    restoredChecked.current = false;
    setRestored(null);
    hadSavedDraft.current = false;
  }, [restoreKey]);

  useEffect(() => {
    if (!restoreOnMount || !userId || restoredChecked.current) return;
    restoredChecked.current = true;
    let cancelled = false;
    const local = readJournalDraftLocal(userId, patientId, scopeKey);
    void fetchServerDraft(patientId, scopeKey)
      .then((server) => {
        if (cancelled) return;
        const chosen = pickNewerJournalDraft(local, server);
        if (chosen && journalDraftHasContent(chosen)) {
          hadSavedDraft.current = true;
          setRestored(chosen);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (local && journalDraftHasContent(local)) {
          hadSavedDraft.current = true;
          setRestored(local);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [restoreOnMount, restoreKey, userId, patientId, scopeKey]);

  useEffect(() => {
    if (!enabled) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      persistLocal();
      const dirty = schedulerRef.current?.isDirty() ?? journalDraftHasContent(latest.current);
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [enabled, persistLocal]);

  const deleteDraft = useCallback(async () => {
    const current = latest.current;
    try {
      await deleteStoredDraft(current.patientId, current.scopeKey, current.userId);
    } catch {
      // Keep localStorage; retry is not needed after a successful note save.
    }
    hadSavedDraft.current = false;
    setRestored(null);
  }, []);

  const dismissRestored = useCallback(() => setRestored(null), []);

  const flush = useCallback(async () => {
    persistLocal();
    await schedulerRef.current?.flush();
  }, [persistLocal]);

  return {
    status,
    lastSavedAt,
    restored,
    dismissRestored,
    deleteDraft,
    flush,
  };
}
